import { useEffect, useMemo, useRef, useState } from "react";
import { PreviewContainer, PreviewLabel, PreviewPanel } from "../style";
import useLanguage from "../../../hooks/useLanguage";
import OverlayLayer from "./OverlayLayer";

const NORMAL_W = 200;
const NORMAL_H = Math.round(NORMAL_W * (16 / 9));

// Linear-interpolate a focus scene's x trajectory (scene-relative times), matching
// reframer.render_from_json `_render_focus` (one crop per frame, lerp between points).
function interpTrajX(traj, t) {
  if (!traj?.length) return 0;
  if (t <= traj[0].time) return traj[0].x;
  const last = traj[traj.length - 1];
  if (t >= last.time) return last.x;
  for (let i = 0; i < traj.length - 1; i++) {
    const a = traj[i];
    const b = traj[i + 1];
    if (t >= a.time && t <= b.time) {
      const span = b.time - a.time;
      return span <= 0 ? b.x : a.x + (b.x - a.x) * ((t - a.time) / span);
    }
  }
  return last.x;
}

/**
 * VerticalPreview — live 9:16 preview rendered to a <canvas> that mirrors the
 * main editor video via drawImage. Using a canvas (instead of a second <video>
 * element bound to the same src) avoids fetching the source video twice — the
 * canvas reuses the already-decoded frames from the main player.
 *
 * Crop selection:
 *   - bouncing scenes → speaker_segments[t] override (per-frame speaker bbox)
 *   - all others      → cropRect (selected scene's crop)
 */
export default function VerticalPreview({ videoRef, cropRect, sourceVideoSize, scenes, overlay, template, expanded = false }) {
  const [{ pages: { reframeEditor: text } }] = useLanguage();

  const canvasRef = useRef(null);

  // Throttled current time for OverlayLayer timing (max 4 updates/sec)
  const [currentTime, setCurrentTime] = useState(0);
  const lastTimeUpdateRef = useRef(0);

  // Available height (px) for sizing the preview when expanded.
  const [viewportH, setViewportH] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 800));
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { panelW, panelH } = useMemo(() => {
    if (!expanded) return { panelW: NORMAL_W, panelH: NORMAL_H };
    // Toolbar (~52) + timeline (148) + label/padding (~60) ⇒ ~260 of chrome.
    const maxH = Math.max(360, viewportH - 260);
    const h = Math.min(640, maxH);
    const w = Math.round(h * (9 / 16));
    return { panelW: w, panelH: h };
  }, [expanded, viewportH]);

  // Refs for use inside the stable RAF closure
  const cropRectRef        = useRef(cropRect);
  const sourceVideoSizeRef = useRef(sourceVideoSize);
  const scenesRef          = useRef(scenes);
  const panelSizeRef       = useRef({ w: panelW, h: panelH });
  useEffect(() => { cropRectRef.current        = cropRect; },        [cropRect]);
  useEffect(() => { sourceVideoSizeRef.current = sourceVideoSize; }, [sourceVideoSize]);
  useEffect(() => { scenesRef.current          = scenes; },          [scenes]);
  useEffect(() => { panelSizeRef.current       = { w: panelW, h: panelH }; }, [panelW, panelH]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const main   = videoRef?.current;
    if (!canvas || !main) return;
    const ctx = canvas.getContext("2d");

    // Crop for the EXACT frame time t — mirrors reframer.render / render_from_json so
    // the preview equals the exported product: scene at t → static box, focus
    // trajectory (linear, scene-relative), or bouncing speaker segment. The
    // selected-scene crop is only the fallback (e.g. while dragging a crop box).
    const cropForTime = (t) => {
      let crop = cropRectRef.current;
      const sceneList = scenesRef.current;
      if (sceneList?.length) {
        const scene =
          sceneList.find((s) => t >= (s.start_time ?? 0) && t < (s.end_time ?? Infinity)) ??
          sceneList[sceneList.length - 1];
        const sceneT = t - (scene?.start_time ?? 0);
        if (scene?.strategy === "bouncing" && scene.speaker_segments?.length) {
          const seg = scene.speaker_segments.find(
            (s) => sceneT >= (s.start ?? 0) && sceneT < (s.end ?? Infinity)
          );
          const segCrop = seg?.crop ?? scene.speaker_segments[0]?.crop;
          if (segCrop?.width > 0) crop = segCrop;
        } else if (scene?.strategy === "focus" && scene.trajectory?.length && scene.crop?.width > 0) {
          crop = { ...scene.crop, x: interpTrajX(scene.trajectory, sceneT) };
        } else if (scene?.crop?.width > 0) {
          crop = scene.crop;
        }
      }
      return crop;
    };

    const draw = (t) => {
      if (main.readyState < 2 || !main.videoWidth) return;
      const crop = cropForTime(t);
      if (crop?.width > 0 && crop?.height > 0) {
        const { w, h } = panelSizeRef.current;
        ctx.drawImage(main, crop.x, crop.y, crop.width, crop.height, 0, 0, w, h);
      }
      // Throttle currentTime state updates to 4/sec to avoid wasted re-renders
      const now = performance.now();
      if (now - lastTimeUpdateRef.current >= 250) {
        lastTimeUpdateRef.current = now;
        setCurrentTime(main.currentTime);
      }
    };

    // Frame-accurate during playback: requestVideoFrameCallback delivers the EXACT
    // presentation time (mediaTime) of the frame being shown, so the crop always
    // matches the picture — this is the editor equivalent of render.py issuing one
    // crop per source frame, and it kills the wrong-crop flash at every cut that
    // rAF (decoupled from the displayed frame) produced. A light rAF loop still
    // covers the PAUSED case (scrub / crop edits), which rVFC doesn't fire for.
    let vfcHandle = null;
    let rafHandle = null;
    if (typeof main.requestVideoFrameCallback === "function") {
      const onFrame = (_now, meta) => {
        draw(meta.mediaTime);
        vfcHandle = main.requestVideoFrameCallback(onFrame);
      };
      vfcHandle = main.requestVideoFrameCallback(onFrame);
      const pausedLoop = () => {
        if (main.paused || main.ended || main.seeking) draw(main.currentTime);
        rafHandle = requestAnimationFrame(pausedLoop);
      };
      rafHandle = requestAnimationFrame(pausedLoop);
    } else {
      const loop = () => {
        draw(main.currentTime);
        rafHandle = requestAnimationFrame(loop);
      };
      rafHandle = requestAnimationFrame(loop);
    }

    return () => {
      if (vfcHandle != null && main.cancelVideoFrameCallback) main.cancelVideoFrameCallback(vfcHandle);
      if (rafHandle != null) cancelAnimationFrame(rafHandle);
    };
  }, [videoRef]);

  return (
    <PreviewPanel $expanded={expanded}>
      <PreviewLabel>{text.preview.label}</PreviewLabel>
      <PreviewContainer style={{ width: panelW, height: panelH }}>
        <canvas
          ref={canvasRef}
          width={panelW}
          height={panelH}
          style={{ display: "block", width: "100%", height: "100%" }}
        />
        {overlay?.enabled && template && (
          <OverlayLayer
            template={template}
            textOverrides={overlay.payload?.textOverrides}
            timing={overlay.payload?.timing}
            containerW={panelW}
            containerH={panelH}
            currentTime={currentTime}
            videoDuration={videoRef?.current?.duration}
          />
        )}
      </PreviewContainer>
    </PreviewPanel>
  );
}
